import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChevronForward } from 'react-icons/io5';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuth } from '../../../context/AuthContext';

// hq_status 기반 집계 — 「추천하기 > 고객 초대 현황」 카드와 동일 기준
// (이전엔 partner_assignments.status 기준이었어서 본사 단계와 어긋났음)

const TRACK_CELLS = [
  { key: 'consult_db', label: '상담DB' },
  { key: 'consult', label: '상담' },
  { key: 'received', label: '접수' },
  { key: 'in_progress', label: '진행' },
  { key: 'on_hold', label: '보류' },
  { key: 'cancelled', label: '취소' },
  { key: 'completed', label: '완료' },
];

const LINKS = [
  { label: '고객 목록 · 리드 조회', path: '/realtor/manage/lead' },
  { label: '가망고객 등록·발송', path: '/realtor/manage/prospect' },
];

const ManageCustomerTab = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [counts, setCounts] = useState(() =>
    TRACK_CELLS.reduce((acc, c) => ({ ...acc, [c.key]: 0 }), {})
  );

  useEffect(() => {
    if (!userData?.uid) return;
    (async () => {
      try {
        const srSnap = await getDocs(query(
          collection(db, 'service_requests'),
          where('source_realtor_id', '==', userData.uid)
        ));

        // 서비스 완료 보강 — hq_status 만으론 완료를 알 수 없다.
        // partner_assignments.status === 'completed' 인 SR 은 본사 정산 단계(settlement_check)에
        // 머물러 있어도 "완료"로 집계한다. (serviceRequestService.getRequestCountsByStatus 와 동일 기준)
        const completedSrIds = new Set();
        try {
          const asgSnap = await getDocs(query(
            collection(db, 'partner_assignments'),
            where('status', '==', 'completed')
          ));
          asgSnap.docs.forEach((a) => {
            const ad = a.data();
            const sid = ad.service_request_id || ad.serviceRequestId;
            if (sid) completedSrIds.add(sid);
          });
        } catch { /* 조회 실패 시 hq_status 기준만 사용 */ }

        const c = { consult_db: 0, consult: 0, received: 0, in_progress: 0, on_hold: 0, cancelled: 0, completed: 0 };
        srSnap.docs.forEach((d) => {
          const data = d.data();
          const s = data.hq_status || data.hqStatus || 'unread';
          c.consult_db += 1;
          if (completedSrIds.has(d.id) || s === 'settlement_done' || s === 'completed') c.completed += 1;
          else if (s === 'unread' || s === 'read') c.consult += 1;
          else if (s === 'assigned') c.received += 1;
          else if (s === 'settlement_check') c.in_progress += 1;
          else if (s === 'hq_review_needed') c.on_hold += 1;
          else if (s === 'cancelled' || s === 'unmatched_cancelled') c.cancelled += 1;
        });
        setCounts(c);
      } catch (e) {
        console.error('[ManageCustomerTab] counts failed:', e);
      }
    })();
  }, [userData?.uid]);

  return (
    <>
      <SectionTitle>고객 DB 추적</SectionTitle>
      <SectionDesc>상담부터 완료까지 전환 흐름을 실시간으로 파악합니다.</SectionDesc>

      <TrackBox>
        <TrackGrid>
          {TRACK_CELLS.slice(0, 3).map((c) => (
            <TrackCell key={c.key}>
              <CellLabel>{c.label}</CellLabel>
              <CellValue>{counts[c.key]}</CellValue>
            </TrackCell>
          ))}
        </TrackGrid>
        <TrackGrid>
          {TRACK_CELLS.slice(3, 6).map((c) => (
            <TrackCell key={c.key}>
              <CellLabel>{c.label}</CellLabel>
              <CellValue>{counts[c.key]}</CellValue>
            </TrackCell>
          ))}
        </TrackGrid>
        <TrackGridSingle>
          <TrackCell>
            <CellLabel>{TRACK_CELLS[6].label}</CellLabel>
            <CellValue>{counts[TRACK_CELLS[6].key]}</CellValue>
          </TrackCell>
        </TrackGridSingle>
      </TrackBox>

      <LinkList>
        {LINKS.map((l) => (
          <LinkRow key={l.path} onClick={() => navigate(l.path)}>
            <LinkText>{l.label}</LinkText>
            <IoChevronForward size={16} color="#9CA3AF" />
          </LinkRow>
        ))}
      </LinkList>
    </>
  );
};

export default ManageCustomerTab;

// ───── 스타일 ─────

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`;

const SectionDesc = styled.p`
  font-size: 13px;
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: 16px;
`;

const TrackBox = styled.div`
  background: #F1F5FF;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TrackGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const TrackGridSingle = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
`;

const TrackCell = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 14px 10px;
  text-align: center;
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
`;

const CellLabel = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CellValue = styled.p`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1;
`;

const LinkList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;

  &:last-child { border-bottom: none; }
`;

const LinkText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;
